import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

import PageLayout from "examples/LayoutContainers/PageLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DefaultPricingCard from "examples/Cards/PricingCards/DefaultPricingCard";
import LanguageSelector from "examples/Navbars/CustomNavbar/LanguageSelector";
import RegisterAffiliateForm from "./registerAffiliateForm";
import { useMaterialUIController } from "context";

import bgImage from "assets/images/bg-register.png";
import config from "config";

const PARENT_KEYS = ["Performers", "ServiceProviders", "Places"];

// Module-level caches — persist across remounts for the lifetime of the browser session
let _categoriesCache = null;
let _plansCache = null;

function StepBubble({ number, active, done }) {
  return (
    <MDBox
      width="2.25rem"
      height="2.25rem"
      borderRadius="50%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      sx={{
        backgroundColor: active || done ? "info.main" : "rgba(0,0,0,0.1)",
        transition: "background-color 0.3s ease",
      }}
    >
      <MDTypography
        variant="caption"
        fontWeight="bold"
        sx={{ lineHeight: 1, color: active || done ? "#fff" : "rgba(0,0,0,0.4)" }}
      >
        {number}
      </MDTypography>
    </MDBox>
  );
}

const SUPPORT_EMAIL = "customer-support@louder.vip";
const SUPPORT_PHONE = "+30 210 440 5749";

function SupportModal({ open, onClose }) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
      disableAutoFocus
    >
      <Card
        sx={{
          width: { xs: "95%", sm: "85%", md: "70%", lg: "60%" },
          maxWidth: 500,
          maxHeight: "90vh",
          overflow: "auto",
          p: 3,
          position: "relative",
          zIndex: 10000,
        }}
      >
        <MDBox>
          <MDTypography variant="h4" mb={2}>
            {t("register.support.modalTitle")}
          </MDTypography>

          <MDTypography variant="body2" color="text" mb={3}>
            {t("register.support.modalBody")}
          </MDTypography>

          <MDBox mb={3} p={2} bgcolor="grey.100" borderRadius="lg">
            <MDTypography variant="button" fontWeight="bold" color="info" display="block" mb={2}>
              {t("register.support.freeYearNote")}
            </MDTypography>

            <MDBox display="flex" flexDirection="column" gap={1.5}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <EmailIcon fontSize="small" sx={{ color: "info.main", flexShrink: 0 }} />
                <MDTypography
                  component="a"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  variant="body2"
                  fontWeight="medium"
                  sx={{ color: "#fff !important", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  {SUPPORT_EMAIL}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={1}>
                <PhoneIcon fontSize="small" sx={{ color: "info.main", flexShrink: 0 }} />
                <MDTypography
                  component="a"
                  href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
                  variant="body2"
                  fontWeight="medium"
                  sx={{ color: "#fff !important", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  {SUPPORT_PHONE}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end">
            <MDButton variant="gradient" color="info" onClick={onClose}>
              {t("common.close")}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </Modal>
  );
}

function PricingPage() {
  const { t, i18n } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const lang = i18n.language;
  const { search: locationSearch } = useLocation();
  const deepLink = useMemo(() => {
    const p = new URLSearchParams(locationSearch);
    return {
      categoryId: p.get("categoryId") || null,
      planKey: p.get("plan") || null,
      promoCode: p.get("promoCode") || null,
      city: p.get("city") || null,
    };
  }, [locationSearch]);

  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState({});
  const [catLoading, setCatLoading] = useState(true);

  // pendingCategory: highlighted in step 1 (not yet confirmed)
  // selectedCategory: confirmed — drives step 1 → 2 transition
  const [pendingCategory, setPendingCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [search, setSearch] = useState("");
  const [tabIndex, setTabIndex] = useState(0); // 0 = All, 1-3 = parent
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    const catReady = _categoriesCache && _categoriesCache.length > 0;
    const plansReady = _plansCache !== null;

    if (catReady && plansReady) {
      setCategories(_categoriesCache);
      setPlans(_plansCache);
      setCatLoading(false);
      return;
    }

    const fetchCats = catReady
      ? Promise.resolve(_categoriesCache)
      : fetch(`${config.BACKEND_BASE_URL}/api/categories?registration=true`)
          .then((r) => r.json())
          .then((data) => {
            const list = Array.isArray(data) ? data : [];
            _categoriesCache = list;
            return list;
          });

    const fetchPlans = plansReady
      ? Promise.resolve(_plansCache)
      : fetch(`${config.BACKEND_BASE_URL}/api/affiliate-plans`)
          .then((r) => r.json())
          .then((data) => {
            const map = {};
            if (Array.isArray(data)) {
              data.forEach((p) => {
                map[`${p.parentCategory}_${p.planKey}`] = p;
              });
            }
            _plansCache = map;
            return map;
          });

    Promise.all([fetchCats, fetchPlans])
      .then(([cats, plansMap]) => {
        setCategories(cats);
        setPlans(plansMap);

        // Deep-link: jump straight to Step 3 if params are present
        if (deepLink.categoryId && deepLink.planKey) {
          const cat = cats.find((c) => String(c._id) === deepLink.categoryId);
          const plan = plansMap[`${cat?.parentCategory}_${deepLink.planKey}`];
          if (cat && plan) {
            setPendingCategory(cat);
            setSelectedCategory(cat);
            setSelectedPlan({ ...plan, planKey: deepLink.planKey });
          }
        }
      })
      .catch((err) => console.error("Failed to fetch register data:", err))
      .finally(() => setCatLoading(false));
  }, []);

  const tabParents = [null, ...PARENT_KEYS]; // null = "All"

  const filtered = categories.filter((cat) => {
    const matchesTab =
      tabIndex === 0 || cat.parentCategory === tabParents[tabIndex];
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      cat.name.toLowerCase().includes(q) ||
      cat.en.toLowerCase().includes(q) ||
      cat.gr.toLowerCase().includes(q);
    return matchesTab && matchesSearch;
  });

  const step = !selectedCategory ? 1 : !selectedPlan ? 2 : 3;

  const handleConfirmCategory = () => {
    if (pendingCategory) setSelectedCategory(pendingCategory);
  };

  const handleChangeCategory = () => {
    setSelectedCategory(null);
    setSelectedPlan(null);
    // keep pendingCategory so the user's previous pick stays highlighted
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handleChangePlan = () => {
    setSelectedPlan(null);
  };

  // Step 2 — build plan cards from AffiliatePlan (authoritative, in sync with Stripe)
  const specsMap = t("pricing.specs", { returnObjects: true });
  const planDefs = selectedCategory
    ? ["y1", "y3", "y1p", "y1p-test"]
        .map((planKey) => {
          const plan = plans[`${selectedCategory.parentCategory}_${planKey}`];
          if (!plan) return null;
          return {
            planKey,
            label: plan.label || t(`register.form.plans.${planKey}`),
            amount: plan.amount,
            intervalCount: plan.intervalCount,
          };
        })
        .filter(Boolean)
    : [];


  const NotFoundLink = () => (
    <MDBox mt={3} textAlign="center">
      <MDTypography
        variant="caption"
        sx={{ cursor: "pointer", textDecoration: "underline", color: "#fff" }}
        onClick={() => setSupportOpen(true)}
      >
        {t("register.support.notFoundLink")}
      </MDTypography>
    </MDBox>
  );

  return (
    <PageLayout>
      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <MDBox
        sx={{
          minHeight: "55vh",
          backgroundImage: ({
            functions: { linearGradient, rgba },
            palette: { black },
          }) =>
            `${linearGradient(
              rgba(black.main, 0.55),
              rgba(black.main, 0.3)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center 20%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={{ xs: 3, md: 5 }}
          pt={3}
        >
          <MDTypography
            variant="h5"
            color="white"
            fontWeight="bold"
            sx={{ letterSpacing: 3, textTransform: "uppercase" }}
          >
            Louder
          </MDTypography>
          <LanguageSelector />
        </MDBox>

        <MDBox
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          px={3}
          pb={6}
        >
          <MDBox maxWidth="560px">
            <MDTypography variant="h2" color="white" fontWeight="bold" mb={1.5}>
              {t("register.pricingHeroTitle")}
            </MDTypography>
            <MDTypography variant="body1" color="white" sx={{ opacity: 0.85 }}>
              {t("register.pricingHeroSubtitle")}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: 7 }}>
        {/* Step indicator */}
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          mb={6}
        >
          <StepBubble number={1} active={step === 1} done={step > 1} />
          <MDTypography
            variant="button"
            fontWeight={step === 1 ? "bold" : "regular"}
            color={step === 1 ? "dark" : "secondary"}
            ml={1}
            mr={2}
          >
            {t("register.stepCategory")}
          </MDTypography>

          <MDBox
            height="2px"
            width={{ xs: "2rem", sm: "3rem" }}
            flexShrink={0}
            sx={{
              backgroundColor: step > 1 ? "info.main" : "rgba(0,0,0,0.12)",
              transition: "background-color 0.4s ease",
            }}
          />

          <StepBubble number={2} active={step === 2} done={step > 2} />
          <MDTypography
            variant="button"
            fontWeight={step === 2 ? "bold" : "regular"}
            color={step === 2 ? "dark" : "secondary"}
            ml={1}
            mr={2}
          >
            {t("register.stepPlan")}
          </MDTypography>

          <MDBox
            height="2px"
            width={{ xs: "2rem", sm: "3rem" }}
            flexShrink={0}
            sx={{
              backgroundColor: step > 2 ? "info.main" : "rgba(0,0,0,0.12)",
              transition: "background-color 0.4s ease",
            }}
          />

          <StepBubble number={3} active={step === 3} done={false} />
          <MDTypography
            variant="button"
            fontWeight={step === 3 ? "bold" : "regular"}
            color={step === 3 ? "dark" : "secondary"}
            ml={1}
          >
            {t("register.stepDetails")}
          </MDTypography>
        </MDBox>

        {/* ── Step 1: Category grid ── */}
        {step === 1 && (
          <Card>
            <MDBox p={{ xs: 3, md: 4 }}>
              <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
                {t("register.category.modalTitle")}
              </MDTypography>
              <MDTypography variant="body2" color="text" mb={3}>
                {t("register.category.modalSubtitle")}
              </MDTypography>

              {/* Search */}
              <MDBox mb={2}>
                <TextField
                  fullWidth
                  placeholder={t("register.category.searchPlaceholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    if (e.target.value) setTabIndex(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              {/* Parent-category tabs */}
              <MDBox mb={3} sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabIndex}
                  onChange={(_, v) => {
                    setTabIndex(v);
                    setSearch("");
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={t("register.allCategories")} />
                  {PARENT_KEYS.map((pk) => (
                    <Tab
                      key={pk}
                      label={t(`register.parentCategories.${pk}`, {
                        defaultValue: pk,
                      })}
                    />
                  ))}
                </Tabs>
              </MDBox>

              {/* Bubbles */}
              {catLoading ? (
                <MDBox display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </MDBox>
              ) : filtered.length === 0 ? (
                <MDBox py={5} textAlign="center">
                  <MDTypography variant="body2" color="text">
                    {t("register.category.noResults")}
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox
                  display="flex"
                  flexWrap="wrap"
                  gap={1.5}
                  py={1}
                  minHeight={120}
                >
                  {filtered.map((cat) => {
                    const isHighlighted = pendingCategory?._id === cat._id;
                    const label = cat[lang] || cat.gr;
                    return (
                      <MDBox
                        key={cat._id}
                        onClick={() => setPendingCategory(cat)}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          height: "36px",
                          px: 2,
                          borderRadius: "12px",
                          border: "1.5px solid #fff",
                          backgroundColor: isHighlighted
                            ? "info.main"
                            : "transparent",
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "info.main",
                            transform: "translateY(-1px)",
                            boxShadow: 2,
                          },
                        }}
                      >
                        <MDTypography
                          variant="button"
                          fontWeight="medium"
                          sx={{
                            lineHeight: 1,
                            color: "#fff",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label}
                        </MDTypography>
                      </MDBox>
                    );
                  })}
                </MDBox>
              )}

              {/* Confirm button */}
              <MDBox
                display="flex"
                justifyContent="flex-end"
                mt={3}
              >
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!pendingCategory}
                  onClick={handleConfirmCategory}
                >
                  {t("register.category.confirmBtn")}
                </MDButton>
              </MDBox>

              <NotFoundLink />
            </MDBox>
          </Card>
        )}

        {/* ── Step 2: Pricing cards ── */}
        {step === 2 && (
          <>
            <MDBox textAlign="center" mb={5}>
              <MDTypography variant="h4" fontWeight="bold" mb={1}>
                {t("register.packagesFor", {
                  category: selectedCategory[lang] || selectedCategory.gr,
                })}
              </MDTypography>
              <MDButton
                variant="text"
                color="info"
                size="small"
                onClick={handleChangeCategory}
              >
                {t("register.changeCategory")}
              </MDButton>
            </MDBox>

            <MDBox
              mb={4}
              px={3}
              py={2}
              borderRadius="lg"
              sx={{
                background: "linear-gradient(135deg, #be185d 0%, #7c3aed 100%)",
                textAlign: "center",
              }}
            >
              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff", fontSize: "0.95rem" }}>
                🔒 {t("register.foundingPricesBanner")}
              </MDTypography>
            </MDBox>

            <Grid container spacing={3} justifyContent="center">
              {planDefs.map((plan) => {
                const isPremium = plan.planKey.endsWith("p");
                const periodLabel =
                  plan.intervalCount === 1
                    ? t("pricing.periodSingle")
                    : t("pricing.periodMultiple", {
                        count: plan.intervalCount,
                      });
                return (
                  <Grid item xs={12} lg={4} key={plan.planKey}>
                    <DefaultPricingCard
                      color="dark"
                      badge={{
                        color: isPremium ? "warning" : "info",
                        label: plan.label,
                      }}
                      price={{
                        currency: "€",
                        value: String(plan.amount ?? "—"),
                        type: periodLabel,
                      }}
                      specifications={specsMap[plan.planKey] ?? []}
                      action={{
                        type: "function",
                        color: isPremium ? "warning" : "info",
                        label: t("pricing.selectBtn"),
                        onClick: () => handleSelectPlan(plan),
                      }}
                      shadow={false}
                    />
                  </Grid>
                );
              })}
            </Grid>

            <NotFoundLink />
          </>
        )}

        {/* ── Step 3: Contact form ── */}
        {step === 3 && (
          <>
            <MDBox textAlign="center" mb={5}>
              <MDButton
                variant="text"
                color="info"
                size="small"
                onClick={handleChangePlan}
              >
                {t("register.changePlan")}
              </MDButton>
            </MDBox>

            <RegisterAffiliateForm
              planKey={selectedPlan.planKey}
              categoryId={selectedCategory._id}
              selectedCategory={selectedCategory}
              plan={selectedPlan}
              onBack={handleChangePlan}
              initialPromoCode={deepLink.promoCode}
              initialCity={deepLink.city}
            />
          </>
        )}

        {/* Sign-in link */}
        <MDBox mt={6} textAlign="center">
          <Divider sx={{ mb: 3 }} />
          <MDTypography variant="body2" color="text">
            {t("auth.haveAccount")}{" "}
            <MDTypography
              component={Link}
              to="/authentication/sign-in"
              variant="body2"
              color="info"
              fontWeight="medium"
              sx={{ cursor: "pointer" }}
            >
              {t("auth.signIn")}
            </MDTypography>
          </MDTypography>
        </MDBox>
      </Container>
    </PageLayout>
  );
}

export default PricingPage;
